/* eslint-disable */
import React, { useState } from "react";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import qs from "qs";

import routes from "./routes/routes";
import theme from "./styles/theme";

/**
 * Redux actions
 */
import { update as updateGuid } from "./store/slices/guid";
import { update as updateScanUser } from "./store/slices/scanUser";
import { addOrUpdate as addOrUpdatePicture } from "./store/slices/gallery";
import { update as updateShowSpinner } from "./store/slices/showSpinner";
import { update as updateCurrentlyCapturing } from "./store/slices/currentlyCapturing";
import { update as updateSubject } from "./store/slices/subject";
import {
  update as updateError,
  clear as clearError,
} from "./store/slices/error";
import { update as updateMode } from "./store/slices/mode";
import { update as updateRetake } from "./store/slices/retake";
import { update as updateToken } from "./store/slices/token";

/**
 * Mitek SDK stuff
 */
import "./mitek/mitekSDK.css";
import sdkEnum from "./mitek/sdkEnum";
import * as mitekScienceSDK from "./mitek/mitek-science-sdk";

/**
 * React components
 */
import Welcome from "./pages/Welcome";
import ChooseDocument from "./pages/ChooseDocument";
import Instructions from "./pages/Instructions";
import ConfirmPicture from "./pages/ConfirmPicture";
import ThankYou from "./pages/ThankYou";
import Error from "./pages/Error";
import ProtectedRoute from "./components/ProtectedRoute";
import api from "./api/api";

/**
 * styled-components styles
 */
const MainPageWrap = styled.main`
  background: #ffffff;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

/**
 * site-wide styles
 */
const GlobalStyle = createGlobalStyle`
    * {
      box-sizing: border-box;
      font-family: ${theme.font}
    }
    legend {
      padding: 0;
      display: table;
    }
    fieldset {
      border: 0;
      padding: 0.01em 0 0 0;
      margin: 0;
      min-width: 0;
    }
    body:not(:-moz-handler-blocked) fieldset {
      display: table-cell;
    }
    body {
      background: #ffffff;
    }
    p {
      font-size: 1.125em;
    }
  `;

function App() {
  const dispatch = useDispatch();

  const history = useHistory();

  // whether the user tried uploading a picture instead of using their camera
  const [uploadedPicture, setUploadedPicture] = useState(false);

  /**
   * Redux values
   */
  const guid = useSelector((state) => state.guid.value);
  const scanUser = useSelector((state) => state.scanUser.value);
  const token = useSelector((state) => state.token.value);
  const mode = useSelector((state) => state.mode.value);
  const subject = useSelector((state) => state.subject.value);
  const autoCaptureOptions = useSelector(
    (state) => state.autoCaptureOptions.value,
  );
  const sdkOptions = useSelector((state) => state.sdkOptions.value);
  const currentlyCapturing = useSelector(
    (state) => state.currentlyCapturing.value,
  );
  const country = useSelector((state) => state.country.value);
  const retakeStatus = useSelector((state) => state.retake.value);
  const manualOnly = useSelector((state) => state.manualOnly.value);

  const sdkResourcePath = `${process.env.PUBLIC_URL}/static/sdk/`;
  let timerId = null;

  /**
   * Get account person and guid from URL query string
   * if they haven't already been gotten
   * and store in redux
   */
  if (!guid || !scanUser || !token) {
    dispatch(updateShowSpinner(true));
    const { search } = useLocation();
    const queryString = qs.parse(search, { ignoreQueryPrefix: true });
    if (!guid) {
      dispatch(updateGuid(queryString.guid));
    }
    if (!scanUser) {
      dispatch(updateScanUser(queryString.scanUser));
    }
    if (!token) {
      dispatch(updateToken(queryString.token));
    }
    api()
      .olaStartScan({
        token: queryString.token,
        guid: queryString.guid,
        scanUser: queryString.scanUser,
      })
      .then((result) => {
        // console.log(result);
        dispatch(updateShowSpinner(false));
      })
      .catch((error) => {
        // console.log(error);
        dispatch(updateShowSpinner(false));
      });
  }

  /**
   * Catalogs the capture session results into a new or existing gallery.pictures array item.
   * @param {object} result The output ob9*8ject from the SDK's FRAME_CAPTURE_RESULT event.
   */
  const handleResult = (result) => {
    // turn off loading state
    dispatch(updateShowSpinner(false));
    // if we were doing a retake, we are now done so mark it as false
    dispatch(updateRetake(false));
    // if the user uploaded the image, kick them to an error screen
    if (
      result &&
      result.response &&
      result.response.mibiData &&
      result.response.mibiData.ImageSource &&
      result.response.mibiData.ImageSource.Upload &&
      result.response.mibiData.ImageSource.Upload === "true"
    ) {
      setUploadedPicture(true);
      history.push(routes.welcome);
      return;
    }

    const newPicture = {
      docType: result.response.docType,
      image: result.response.imageData,
      mibiData: result.response.mibiData,
      decodedStr: result.response.code,
      documentCaptured: currentlyCapturing,
      modeUsed: mode,
    };
    // add a label to display on the confirm picture page
    if (newPicture.documentCaptured === "driver's license front") {
      newPicture.label = "Front";
    }
    if (newPicture.documentCaptured === "driver's license back") {
      newPicture.label = "Back";
    }
    if (newPicture.documentCaptured === "passport") {
      newPicture.label = "Passport";
    }
    // clone the object, otherwise the SDK will error out
    const newPictureCopy = JSON.parse(JSON.stringify(newPicture));
    dispatch(addOrUpdatePicture(newPictureCopy));

    // if manual capture was used, turn auto capture back on for the next picture
    if (mode === "MANUAL_CAPTURE" && !manualOnly) {
      dispatch(updateMode("AUTO_CAPTURE"));
    }

    /**
     * if the user was capturing the front of a driver's license
     * and it wasn't a retake
     * send them to the instruction page for the back of the license
     * otherwise send them to the confirmation page
     */
    // console.log("currently capturing", currentlyCapturing);
    // console.log("retaking?", retakeStatus);
    if (currentlyCapturing === "driver's license front" && !retakeStatus) {
      dispatch(updateCurrentlyCapturing("driver's license back"));
      if (country === "usa-or-canada") {
        dispatch(updateSubject("PDF417_BARCODE"));
      }
      if (country === "other") {
        dispatch(updateSubject("DL_FRONT"));
      }
      history.push(routes.instructions);
    } else {
      history.push(routes.confirmPicture);
    }
  };

  const handleException = (exception) => {
    if (exception && exception.message) {
      dispatch(updateError({ type: "unknown", event: exception.message }));
    }
    if (exception && exception.type && exception.event) {
      dispatch(updateError(exception));
    }
    dispatch(updateShowSpinner(false));
    history.push(routes.error);
  };

  /**
   * Invokes the SDK to start a Manual or Auto capture.
   */
  const startCaptureSession = () => {
    // set loading state
    dispatch(updateShowSpinner(true));
    // reset error state
    dispatch(clearError());
    /**
     * AUTO_CAPTURE frames started processing. add any viewport layout elements
     */
    mitekScienceSDK.on("FRAME_PROCESSING_STARTED", () => {
      if (autoCaptureOptions.showCancel) {
        addCancelButton();
      }
    });

    /**
     * AUTO_CAPTURE camera started
     */
    mitekScienceSDK.on("CAMERA_DISPLAY_STARTED", () => {
      dispatch(updateShowSpinner(true));

      timerId = setTimeout(() => {
        clearTimeout(timerId);
        mitekScienceSDK.cmd("SDK_STOP");
        handleException({ event: "TIMEOUT", type: "TIMEOUT" });
      }, autoCaptureOptions.timeoutSec * 1000);

      // show the first initial hint message
      mitekScienceSDK.cmd(
        "SHOW_HINT",
        sdkEnum.customMessages[subject].firstMessage,
      );
    });

    /**
     * MANUAL_CAPTURE only. Show the spinner
     */
    mitekScienceSDK.on("IMAGE_CAPTURED", () => {
      // console.log("image captured");
      dispatch(updateShowSpinner(false));
    });

    /**
     * AUTO_CAPTURE show hints to user
     * MANUAL-CAPTURE handle bad input
     */
    mitekScienceSDK.on("FRAME_PROCESSING_FEEDBACK", (status) => {
      if ("AUTO_CAPTURE" === mode) {
        if (status.key !== null) {
          mitekScienceSDK.cmd(
            "SHOW_HINT",
            "MITEK_ERROR_FOUR_CORNER" === status.key
              ? sdkEnum.customMessages[subject].fourCornerMessage
              : sdkEnum.autoCaptureHints[status.key],
          );
        }
      }

      if ("MANUAL_CAPTURE" === mode) {
        // Only return exceptions that don't accompany a result
        // FRAME_CAPTURE_RESULT encloses the same object
        if (
          "CORRUPT_IMAGE" === status.key ||
          "IMAGE_SMALLER_THAN_MIN_SIZE" === status.key
        ) {
          handleException({ type: status.key, event: "SDK_ERROR" });
        }
      }
    });

    /**
     * AUTO_CAPTURE clear the timeout object and call SDK_STOP
     * MANUAL_CAPTURE hide spinner
     */
    mitekScienceSDK.on("FRAME_CAPTURE_RESULT", (result) => {
      if ("AUTO_CAPTURE" === mode) {
        clearTimeout(timerId);
        mitekScienceSDK.cmd("SDK_STOP");
      }
      if ("MANUAL_CAPTURE" === mode) {
        // console.log("Manual capture result", result);
        clearTimeout(timerId);
      }
      handleResult(result);
    });

    /**
     * error handling
     */
    // problem starting session
    mitekScienceSDK.on("SDK_ERROR", (result) => {
      dispatch(updateError({ type: result[0].type, event: "SDK_ERROR" }));
      dispatch(updateShowSpinner(false));
      history.push(routes.error);
    });

    mitekScienceSDK.cmd("CAPTURE_AND_PROCESS_FRAME", {
      mode: mode,
      documentType: subject,
      mitekSDKPath: sdkResourcePath,
      options: sdkOptions,
    });
  };

  /**
   * If the user hasn't chosen country and documentType
   * ProtectedRoute component will redirect to the welcome screen
   * Keeps user from going to a random screen and having the app look broken
   * ThemeProvider passes the styled-components theme to child components
   * GlobalStyle sets site-wide styling
   */
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <MainPageWrap>
        <Switch>
          <Route exact path={routes.chooseDocument}>
            <ChooseDocument></ChooseDocument>
          </Route>
          <ProtectedRoute exact path={routes.instructions}>
            <Instructions
              startCaptureSession={startCaptureSession}
            ></Instructions>
          </ProtectedRoute>
          <ProtectedRoute exact path={routes.confirmPicture}>
            <ConfirmPicture></ConfirmPicture>
          </ProtectedRoute>
          <ProtectedRoute exact path={routes.thankYou}>
            <ThankYou></ThankYou>
          </ProtectedRoute>
          <ProtectedRoute exact path={routes.error}>
            <Error></Error>
          </ProtectedRoute>
          <Route>
            <Welcome uploadedPicture={uploadedPicture}></Welcome>
          </Route>
        </Switch>
      </MainPageWrap>
    </ThemeProvider>
  );
}

export default App;
